'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useTranslations } from 'next-intl';

import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import { useViewerRole } from '@/components/Contexts/ViewerRoleContext';
import { useWorkbenchConfig } from '@/components/Contexts/WorkbenchConfigContext';
import { showErrorToast } from '@/components/ui/ToastNotification';
import { useApiClient } from '@/hooks/useApiClient';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { WIX_USER_NAME_SEPARATOR } from '@/utils/replierDisplayName';

import type { CurrentUser } from '@/hooks/useCurrentUser';
import type { Replier } from '@/services/comments/types';
import type {
  CommentReply,
  Pin,
  ProxyCommentRepliedMessage,
  ProxyReplyToCommentFailedMessage,
  ProxyReplyToCommentMessage,
} from '@/types/feedback';

interface ReplyToCommentResult {
  replies: CommentReply[];
  revision: string | null;
}

/**
 * The owner's optimistic reply is named as the server will name it ("user @
 * account"), taken from one of their loaded replies, else after the signed-in
 * user, so the name and avatar don't jump when the server's reply lands.
 */
function optimisticOwnerReplier(
  pins: Pin[],
  apiUser: CurrentUser | undefined,
): Replier {
  // Only a server-named reply with both halves: a local one, or one named
  // after just the user or the account, would itself jump.
  const knownName = pins
    .flatMap((pin) => pin.replies ?? [])
    .find(
      (r) =>
        apiUser?.id &&
        !r.isLocalOnly &&
        r.replier?.wixUserOptions?.userId === apiUser.id &&
        r.replier.displayName?.includes(WIX_USER_NAME_SEPARATOR),
    )?.replier?.displayName;
  const displayName =
    knownName ||
    [apiUser?.name, apiUser?.accountName]
      .filter(Boolean)
      .join(WIX_USER_NAME_SEPARATOR);
  return {
    type: 'WIX_USER',
    ...(apiUser?.id && { wixUserOptions: { userId: apiUser.id } }),
    ...(displayName && { displayName }),
  };
}

export function useReplyPersistence() {
  const t = useTranslations();
  const {
    state: { pins },
    dispatch,
  } = useCommentsContext();
  // Read at send time, so addReply isn't re-created on every pins change.
  const pinsRef = useRef(pins);
  useEffect(() => {
    pinsRef.current = pins;
  }, [pins]);
  const { isPartner } = useViewerRole();
  const { data: apiUser } = useCurrentUser();
  // feedbackId, metaSiteId and isOwner all travel inside the
  // sealed context. The partner path deliberately does NOT forward the sealed
  // context over postMessage: the proxy uses the owner context its own page
  // minted, so this window cannot escalate a reply to isOwner.
  const { sealedContext } = useWorkbenchConfig();
  const apiClient = useApiClient();

  useEffect(() => {
    if (!isPartner) return;

    function handleReplied(event: MessageEvent) {
      if (event.data?.type !== 'FB_COMMENT_REPLIED') return;
      const { pinId, replies, revision } = (
        event.data as ProxyCommentRepliedMessage
      ).payload;
      dispatch({
        type: 'UPDATE_PIN_REPLIES',
        payload: { pinId, replies, revision },
      });
    }

    function handleFailed(event: MessageEvent) {
      if (event.data?.type !== 'FB_REPLY_TO_COMMENT_FAILED') return;
      const { pinId, optimisticReplyId } = (
        event.data as ProxyReplyToCommentFailedMessage
      ).payload;
      dispatch({
        type: 'REMOVE_LOCAL_REPLY',
        payload: { pinId, localId: optimisticReplyId },
      });
      showErrorToast(t('get-feedback-toast-addReplyFailed'));
    }

    window.addEventListener('message', handleReplied);
    window.addEventListener('message', handleFailed);
    return () => {
      window.removeEventListener('message', handleReplied);
      window.removeEventListener('message', handleFailed);
    };
  }, [isPartner, dispatch, t]);

  const addReply = useCallback(
    async (pin: Pin, message: string) => {
      if (!pin.commentId) return;
      const previousReplyCount = (pin.replies ?? []).filter(
        (r) => !r.isLocalOnly,
      ).length;
      const optimisticId = Date.now();
      dispatch({
        type: 'ADD_REPLY',
        payload: {
          pinId: pin.id,
          reply: {
            localId: optimisticId,
            replier: isPartner
              ? optimisticOwnerReplier(pinsRef.current, apiUser)
              : { type: 'COMMENTER' },
            message,
            createdDate: new Date(),
            isLocalOnly: true,
          },
        },
      });

      if (isPartner) {
        const msg: ProxyReplyToCommentMessage = {
          type: 'FB_REPLY_TO_COMMENT',
          payload: {
            pinId: pin.id,
            optimisticReplyId: optimisticId,
            commentId: pin.commentId,
            message,
            revision: pin.revision,
          },
        };
        window.postMessage(msg, window.location.origin);
        return;
      }

      try {
        const result = await apiClient
          .post<ReplyToCommentResult>('api/reply-to-comment', {
            ctx: sealedContext,
            commentId: pin.commentId,
            message,
            revision: pin.revision,
          })
          .then((r) => r.data);

        // Replies are appended, so look only past the persisted replies the
        // pin already had. When the new one is the reviewer's, take the
        // server list so it stays deletable. Otherwise keep the local reply
        // until comments reload — at the cost of no delete on it.
        const persistedOwnReply = result.replies
          .slice(previousReplyCount)
          .some((r) => r.replier?.type === 'COMMENTER');

        dispatch(
          persistedOwnReply
            ? {
                type: 'UPDATE_PIN_REPLIES',
                payload: {
                  pinId: pin.id,
                  replies: result.replies,
                  revision: result.revision,
                },
              }
            : {
                type: 'REPLY_PERSISTED',
                payload: { pinId: pin.id, revision: result.revision },
              },
        );
      } catch {
        dispatch({
          type: 'REMOVE_LOCAL_REPLY',
          payload: { pinId: pin.id, localId: optimisticId },
        });
        showErrorToast(t('get-feedback-toast-addReplyFailed'));
      }
    },
    [apiClient, dispatch, isPartner, sealedContext, apiUser, t],
  );

  return { addReply };
}
